// ===== BEGIN FILE: src/utils/bhavaSandhi.ts =====
/**
 * Bhava Sandhi (Cusp Proximity) Strength Multiplier
 * 
 * Traditional Vedic concept: A planet's ability to deliver results depends on 
 * its position within the house (bhava).
 * 
 * - Near cusp (0-5°): Bhava Sandhi - transitional, weak, delayed results (0.8x)
 * - Early house (5-10°): Stabilizing, moderate strength (0.9x)
 * - Mid-house (10-20°): Peak strength, full expression (1.1x)
 * - Late house (20-25°): Strong but transitioning (1.0x)
 * - Near next cusp (25-30°): Transitional to next house (0.85x)
 * 
 * This multiplier affects:
 * - Result-giving probability
 * - Extent of impact on house matters
 * - Timing of manifestation
 * 
 * DOES NOT AFFECT: Aspects (aspects are house-to-house, not degree-based)
 */

/**
 * Get bhava sandhi strength multiplier based on planet's position in house
 * @param offsetDeg - Degrees into the house (0-29.99)
 * @returns Multiplier (0.8 to 1.1)
 */
export function getBhavaSandhiMultiplier(offsetDeg: number): number {
  if (offsetDeg < 0 || offsetDeg >= 30) {
    // Invalid offset, return neutral
    return 1.0;
  }

  // Bhava Sandhi zones
  if (offsetDeg < 3) {
    // 0-3°: Critical bhava sandhi (very weak)
    return 0.8;
  } else if (offsetDeg < 5) {
    // 3-5°: Bhava sandhi zone (weak)
    return 0.85;
  } else if (offsetDeg < 10) {
    // 5-10°: Early house (stabilizing)
    return 0.9;
  } else if (offsetDeg < 12) {
    // 10-12°: Entering peak
    return 1.0;
  } else if (offsetDeg < 18) {
    // 12-18°: Peak strength zone
    return 1.1;
  } else if (offsetDeg < 20) {
    // 18-20°: Exiting peak
    return 1.0;
  } else if (offsetDeg < 25) {
    // 20-25°: Late house (still strong)
    return 1.0;
  } else if (offsetDeg < 27) {
    // 25-27°: Approaching next cusp
    return 0.9;
  } else {
    // 27-30°: Near next cusp (transitional)
    return 0.85;
  }
}

/**
 * Apply bhava sandhi multiplier to planet strength
 * Only applies to OCCUPYING planets, not aspecting planets
 * 
 * @param planetStrength - Base strength (-100 to +100)
 * @param offsetDeg - Degrees into house (0-29.99)
 * @param isOccupying - True if planet occupies the house, false if only aspecting
 * @returns Adjusted strength
 */
export function applyBhavaSandhiToStrength(
  planetStrength: number,
  offsetDeg: number | undefined,
  isOccupying: boolean = true
): number {
  // Only apply to occupying planets
  if (!isOccupying || offsetDeg === undefined) {
    return planetStrength;
  }

  const multiplier = getBhavaSandhiMultiplier(offsetDeg);
  return planetStrength * multiplier;
}

/**
 * Get bhava sandhi description for UI display
 */
export function getBhavaSandhiDescription(offsetDeg: number): {
  strength: 'Very Weak' | 'Weak' | 'Moderate' | 'Strong' | 'Peak';
  description: string;
  multiplier: number;
} {
  const multiplier = getBhavaSandhiMultiplier(offsetDeg);
  
  if (multiplier <= 0.85) {
    return {
      strength: 'Very Weak',
      description: 'Bhava Sandhi - transitional zone, delayed/mixed results',
      multiplier
    };
  } else if (multiplier < 0.95) {
    return {
      strength: 'Weak',
      description: 'Stabilizing - results emerging but not yet full',
      multiplier
    };
  } else if (multiplier < 1.05) {
    return {
      strength: 'Strong',
      description: 'Good strength - clear results',
      multiplier
    };
  } else {
    return {
      strength: 'Peak',
      description: 'Peak zone - maximum expression and impact',
      multiplier
    };
  }
}
